import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { TRANSLATION_KEYWORDS_EN_TOP as TRANSLATION_KEYWORDS_EN } from "@/content/translation-corpus/top.gen";
import type { TranslationFaqItem } from "@/content/translation-corpus/faq-types";
import { TRANSLATION_FAQ_CATEGORIES, getTranslationCategoryDef } from "@/content/translation-corpus/faq-defs";

export const Route = createFileRoute("/en/translation/faq/$category")({
  loader: async ({ params }) => {
    const { getTranslationFaqsByCategoryEn } = await import("@/content/translation-corpus/categories-en");
    const def = getTranslationCategoryDef(params.category);
    if (!def) throw notFound();
    return { def, faqs: getTranslationFaqsByCategoryEn(def.slug) };
  },
  component: TranslationFaqCategoryEn,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Category not found" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { def, faqs } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `${def.titleEn} — Certified Translation FAQ (${faqs.length} answers, 2026)`,
        description: def.descEn,
        path: `/en/translation/faq/${params.category}`,
        type: "article",
        alternates: [{ lang: "th", path: `/translation/faq/${params.category}` }],
        keywords: TRANSLATION_KEYWORDS_EN.slice(0, 60),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Certified Translation FAQ", url: `${site}/en/translation/faq` },
              { name: def.titleEn, url: `${site}/en/translation/faq/${params.category}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/translation/faq/${params.category}`)) },
      ],
    };
  },
});

function TranslationFaqCategoryEn() {
  const { def, faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/translation/faq" className="hover:underline">Certified Translation FAQ</a> <span>/</span>{" "}
          <span>{def.titleEn}</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">{def.titleEn}</h1>
        <p className="text-muted-foreground mb-6">{def.descEn}</p>
        <p className="text-sm text-muted-foreground mb-8">
          <strong>{faqs.length}</strong> attorney-written answers in this topic — updated for 2026.
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-xs">
          {TRANSLATION_FAQ_CATEGORIES.map((c) => (
            <a
              key={c.slug}
              href={`/en/translation/faq/${c.slug}`}
              className={`rounded-full border px-3 py-1 ${c.slug === def.slug ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground hover:bg-muted"}`}
            >
              {c.titleEn}
            </a>
          ))}
        </div>

        <div className="space-y-4">
          {faqs.map((item: TranslationFaqItem, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
          {faqs.length === 0 && (
            <p className="rounded-lg border bg-muted/30 p-6 text-sm text-muted-foreground">
              No answers in this topic yet — <a href="/contact" className="text-primary underline">ask our team</a>.
            </p>
          )}
        </div>
      
        <FaqSourceNote topic="translation" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
